<script setup lang="ts">
import type { BetHistoryEntity, BetHistoryEntitySelection } from '@/common/api/model';
import TransactionItem from './TransactionItem.vue';
import { computed } from 'vue';
import { useTransactionDisplay } from './composables/useTransactionDisplay';
import { useErrorCardCustomisation } from './composables/useTransactionListCustomisation';
import * as SUtils from '@/common/customisation/utils/styleUtils';
import { tConversation } from '@/plugins/i18n';

export interface BetTransactionItemWithEventsProps {
  transaction: BetHistoryEntity;
  isSelected: boolean;
  editable?: boolean;
  showEventSelection?: boolean;
  selectedUserEvents?: BetHistoryEntitySelection[];
  validateSelectionForUserEvents?: boolean;
}

const props = withDefaults(defineProps<BetTransactionItemWithEventsProps>(), {
  showEventSelection: false,
  selectedUserEvents: () => [],
  validateSelectionForUserEvents: false,
});

const { getBetSelectionDisplay } = useTransactionDisplay();

const shouldShowUserEvents = computed(() => {
  return props.showEventSelection && props.isSelected;
});

const emit = defineEmits<{
  (e: 'toggle'): void;
  (e: 'toggle-user-event', selection: BetHistoryEntitySelection): void;
}>();

const isUserEventSelected = (selection: BetHistoryEntitySelection): boolean => {
  return props.selectedUserEvents.some((s) => s.id === selection.id);
};

// Transform a selection into a simple transaction-like object for display
const transformSelectionToTransaction = (selection: BetHistoryEntitySelection) => {
  return getBetSelectionDisplay(selection, props.transaction);
};

const errorCard = useErrorCardCustomisation();
const warningStyle = computed(() =>
  SUtils.combineStyles(
    SUtils.createBackgroundStyle(errorCard.backgroundColor),
    SUtils.createBorderStyle(errorCard.borderColor)
  )
);
const warningTextStyle = computed(() => SUtils.createTextStyle(errorCard.textColor));
</script>

<template>
  <div class="bet-transaction-item-with-events">
    <TransactionItem
      :transaction="transaction"
      :editable="editable"
      :is-selected="isSelected"
      @toggle="emit('toggle')"
    />

    <!-- User Events Selections (nested under selected transaction) -->
    <div v-if="shouldShowUserEvents" class="bet-transaction-item-with-events__user-events">
      <div
        v-if="validateSelectionForUserEvents && !selectedUserEvents.length"
        class="bet-transaction-item-with-events__warning card-radius theme-font-subtext"
        :style="warningStyle"
      >
        <span :style="warningTextStyle">{{ tConversation('page.issue.bet.select_at_least_one_selection') }}</span>
      </div>

      <TransactionItem
        v-for="selection in transaction.selections"
        :key="selection.id"
        :transaction="selection"
        :display-data="transformSelectionToTransaction(selection)"
        :editable="editable"
        :is-selected="isUserEventSelected(selection)"
        @toggle="emit('toggle-user-event', selection)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.bet-transaction-item-with-events {
  display: flex;
  flex-direction: column;
}

.bet-transaction-item-with-events__user-events {
  margin-left: 16px;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bet-transaction-item-with-events__warning {
  padding: 12px;
  border: 1px solid;
}
</style>
